/* eslint-disable indent */
import {
  CheckOutlined,
  CopyOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  TeamOutlined,
  UndoOutlined
} from "@ant-design/icons";
import {
  Button,
  Input,
  Popconfirm,
  Progress,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  ACTIVE_DISPATCH_STATES,
  DISPATCH_STATE_TAG_COLORS,
  ENTRY_COUNT_FIELDS,
  WAITLIST_API_ENDPOINTS,
  WAITLIST_TYPE_LABELS,
  WAITLIST_TYPE_TAG_COLORS
} from "./constants";
import CreateEditConfigModal from "./CreateEditConfigModal";
import {
  buildWaitlistJoinUrl,
  computeDispatchProgressPercent,
  copyToClipboard,
  dispatchProgressStatus,
  isDispatchableType
} from "./helper";
import { WaitlistDetail } from "./WaitlistDetail";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";

const { Text, Title } = Typography;

// ─── Entry counts column renderer ─────────────────────────────────────────────

const EntryCounts = ({ entryCounts = {} }) => {
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "nowrap" }}>
      {ENTRY_COUNT_FIELDS.map(({ key, label, color }) => {
        const count = entryCounts[key] ?? 0;
        return (
          <div key={key} style={{ textAlign: "center", minWidth: 36 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.2,
                color: count > 0 ? color : undefined
              }}
            >
              {count}
            </div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {label}
            </Text>
          </div>
        );
      })}
    </div>
  );
};

// ─── WaitlistDashboard ─────────────────────────────────────────────────────────

const WaitlistDashboard = ({ ROOT_URL }) => {
  const [waitlistConfigs, setWaitlistConfigs] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [waitlistFormOptions, setWaitlistFormOptions] = useState({});
  const [isRefreshingFormOptions, setIsRefreshingFormOptions] = useState(false);

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isSubmittingConfig, setIsSubmittingConfig] = useState(false);
  const [activeWaitlistConfig, setActiveWaitlistConfig] = useState(null);

  const [archivingConfigId, setArchivingConfigId] = useState(null);
  const [unarchivingConfigId, setUnarchivingConfigId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [copiedConfigId, setCopiedConfigId] = useState(null);

  const handleCopyJoinLinkForRecord = (record) => {
    const joinUrl = record?.uniquePublicId ? buildWaitlistJoinUrl(record.uniquePublicId) : "";
    if (!joinUrl) {
      return;
    }
    copyToClipboard(joinUrl, () => {
      setCopiedConfigId(record._id);
      showSuccessNotification("Join link copied!");
      setTimeout(() => setCopiedConfigId(null), 1200);
    });
  };

  // Toggles the list between the live waitlists and the archived ones (backend ?archived filter).
  const [showArchived, setShowArchived] = useState(false);

  // Selection lives in the URL (?waitlist=<slug>) so a refresh keeps the detail view open.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSlug = searchParams.get("waitlist");

  const openWaitlistDetail = useCallback(
    (record) => setSearchParams({ waitlist: record.uniquePublicId }),
    [setSearchParams]
  );
  const closeWaitlistDetail = useCallback(() => setSearchParams({}), [setSearchParams]);

  // Derived — always reflects latest fetched data
  const selectedWaitlist = selectedSlug
    ? waitlistConfigs.find((c) => c.uniquePublicId === selectedSlug) ?? null
    : null;

  const normalizedSearch = searchText.trim().toLowerCase();
  const visibleWaitlistConfigs = useMemo(() => {
    if (!normalizedSearch) {
      return waitlistConfigs;
    }
    return waitlistConfigs.filter(
      ({ name, uniquePublicId }) =>
        name?.toLowerCase().includes(normalizedSearch) ||
        uniquePublicId?.includes(normalizedSearch)
    );
  }, [waitlistConfigs, normalizedSearch]);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchWaitlistConfigs = useCallback(() => {
    setIsFetching(true);
    axios
      .get(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}`, {
        params: showArchived ? { archived: "true" } : {}
      })
      .then((res) => setWaitlistConfigs(res.data.configs || []))
      .catch(showErrorNotification)
      .finally(() => setIsFetching(false));
  }, [ROOT_URL, showArchived]);

  const fetchWaitlistFormOptions = useCallback(() => {
    setIsRefreshingFormOptions(true);
    axios
      .get(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.FORM_OPTIONS}`)
      .then((res) => setWaitlistFormOptions(res.data || {}))
      .catch(showErrorNotification)
      .finally(() => setIsRefreshingFormOptions(false));
  }, [ROOT_URL]);

  // ── Config modal handlers ────────────────────────────────────────────────────

  const handleOpenCreateModal = () => {
    setActiveWaitlistConfig(null);
    setIsConfigModalOpen(true);
  };

  const handleOpenEditModal = (waitlistConfig) => {
    setActiveWaitlistConfig(waitlistConfig);
    setIsConfigModalOpen(true);
  };

  const handleSubmitConfig = (payload) => {
    setIsSubmittingConfig(true);
    const request = activeWaitlistConfig
      ? axios.patch(
          `${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${activeWaitlistConfig._id}`,
          payload
        )
      : axios.post(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}`, payload);

    request
      .then(() => {
        showSuccessNotification(activeWaitlistConfig ? "Waitlist updated!" : "Waitlist created!");
        setIsConfigModalOpen(false);
        fetchWaitlistConfigs();
      })
      .catch(showErrorNotification)
      .finally(() => setIsSubmittingConfig(false));
  };

  const handleArchiveConfig = ({ _id, uniquePublicId }) => {
    setArchivingConfigId(_id);
    axios
      .delete(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${_id}`)
      .then(() => {
        showSuccessNotification("Waitlist archived!");
        fetchWaitlistConfigs();
        // Navigate back to list if this config was selected
        if (selectedSlug === uniquePublicId) {
          closeWaitlistDetail();
        }
      })
      .catch(showErrorNotification)
      .finally(() => setArchivingConfigId(null));
  };

  const handleUnarchiveConfig = ({ _id }) => {
    setUnarchivingConfigId(_id);
    axios
      .post(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${_id}/unarchive`)
      .then(() => {
        showSuccessNotification("Waitlist unarchived!");
        fetchWaitlistConfigs();
      })
      .catch(showErrorNotification)
      .finally(() => setUnarchivingConfigId(null));
  };

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchWaitlistConfigs();
  }, [fetchWaitlistConfigs]);

  useEffect(() => {
    fetchWaitlistFormOptions();
  }, [fetchWaitlistFormOptions]);

  // ── Table columns ────────────────────────────────────────────────────────────

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 280,
      sorter: (recordA, recordB) => (recordA.name || "").localeCompare(recordB.name || ""),
      render: (name, record) => (
        <div>
          <Text
            strong
            style={{ cursor: "pointer", color: "#1677ff" }}
            onClick={() => openWaitlistDetail(record)}
          >
            {name}
          </Text>
          <Text type="secondary" style={{ display: "block", fontSize: 11, marginTop: 1 }}>
            {record.uniquePublicId}
          </Text>
        </div>
      )
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 200,
      filters: Object.entries(WAITLIST_TYPE_LABELS).map(([value, text]) => ({ text, value })),
      onFilter: (value, record) => record.type === value,
      render: (type) => (
        <Tag color={WAITLIST_TYPE_TAG_COLORS[type]}>{WAITLIST_TYPE_LABELS[type]}</Tag>
      )
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      filters: [
        { text: "Active", value: true },
        { text: "Inactive", value: false }
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (isActive) => (
        <Tag color={isActive ? "green" : "default"}>{isActive ? "Active" : "Inactive"}</Tag>
      )
    },
    {
      title: "Entries",
      key: "entryCounts",
      width: 260,
      render: (_, record) => <EntryCounts entryCounts={record.entryCounts} />
    },
    {
      title: "Last Dispatch",
      key: "latestDispatch",
      width: 200,
      render: (_, record) => {
        const { latestDispatch } = record;
        if (!latestDispatch) {
          return (
            <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
              None
            </Text>
          );
        }
        const isActive = ACTIVE_DISPATCH_STATES.includes(latestDispatch.state);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <Tag
              color={DISPATCH_STATE_TAG_COLORS[latestDispatch.state]}
              style={{ width: "fit-content" }}
            >
              {latestDispatch.state}
            </Tag>
            {isActive && (
              <Progress
                percent={computeDispatchProgressPercent(latestDispatch)}
                status={dispatchProgressStatus(latestDispatch.state)}
                size="small"
                showInfo={false}
              />
            )}
            <Text type="secondary" style={{ fontSize: 11 }}>
              {latestDispatch.createdAt
                ? dayjs(latestDispatch.createdAt).format("DD MMM YYYY")
                : ""}
            </Text>
          </div>
        );
      }
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      defaultSortOrder: "descend",
      sorter: (recordA, recordB) =>
        new Date(recordA.createdAt).valueOf() - new Date(recordB.createdAt).valueOf(),
      render: (createdAt) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {createdAt ? dayjs(createdAt).format("DD MMM YYYY") : "--"}
        </Text>
      )
    },
    {
      title: "Actions",
      key: "actions",
      width: 280,
      render: (_, record) => {
        const hasActiveDispatch = ACTIVE_DISPATCH_STATES.includes(record.latestDispatch?.state);
        const supportsDispatch = isDispatchableType(record.type);
        const isDispatchDisabled =
          !supportsDispatch || hasActiveDispatch || (record.entryCounts?.pending ?? 0) === 0;
        const dispatchDisabledReason = !supportsDispatch
          ? "This waitlist type does not dispatch"
          : hasActiveDispatch
          ? "Dispatch in progress"
          : "No pending entries";

        return (
          // Stop row-click navigation from firing when interacting with the action buttons.
          <Space size={6} style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="View entries & manage">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => openWaitlistDetail(record)}
              >
                View
              </Button>
            </Tooltip>
            <Tooltip title="Edit waitlist">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleOpenEditModal(record)}
              />
            </Tooltip>
            <Tooltip title={copiedConfigId === record._id ? "Copied!" : "Copy Join Link"}>
              <Button
                size="small"
                icon={copiedConfigId === record._id ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => handleCopyJoinLinkForRecord(record)}
              />
            </Tooltip>
            {/* Always rendered so every row's action strip lines up, even for non-dispatchable types. */}
            <Tooltip
              title={isDispatchDisabled ? dispatchDisabledReason : "Dispatch pending entries"}
            >
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                disabled={isDispatchDisabled}
                onClick={() => openWaitlistDetail(record)}
              />
            </Tooltip>
            {record.archived ? (
              <Popconfirm
                title="Unarchive Waitlist"
                description="This restores the waitlist to the live list and its public pages. All entries and links are intact."
                onConfirm={() => handleUnarchiveConfig(record)}
                okText="Unarchive"
                cancelText="Cancel"
              >
                <Button
                  size="small"
                  icon={<UndoOutlined />}
                  loading={unarchivingConfigId === record._id}
                >
                  Unarchive
                </Button>
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Archive Waitlist"
                description="This is a reversible archive: entries, dispatch history, and every link are kept. It just leaves the live list and stops accepting new joins until you unarchive it."
                onConfirm={() => handleArchiveConfig(record)}
                okText="Archive"
                cancelText="Cancel"
              >
                <Button
                  size="small"
                  icon={<InboxOutlined />}
                  loading={archivingConfigId === record._id}
                >
                  Archive
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  // Detail view
  if (selectedWaitlist) {
    return (
      <>
        <WaitlistDetail
          waitlistConfig={selectedWaitlist}
          ROOT_URL={ROOT_URL}
          onBack={closeWaitlistDetail}
          onDispatchTriggered={fetchWaitlistConfigs}
          onEdit={() => handleOpenEditModal(selectedWaitlist)}
        />
        {isConfigModalOpen && (
          <CreateEditConfigModal
            isModalOpen={isConfigModalOpen}
            isEdit={Boolean(activeWaitlistConfig)}
            isSubmitting={isSubmittingConfig}
            initialValues={activeWaitlistConfig || {}}
            formOptions={waitlistFormOptions}
            setIsModalOpen={setIsConfigModalOpen}
            handleSubmit={handleSubmitConfig}
            refreshFormOptions={fetchWaitlistFormOptions}
            isRefreshingFormOptions={isRefreshingFormOptions}
          />
        )}
      </>
    );
  }

  // List view
  return (
    <div style={{ padding: "20px 32px" }}>
      {/* ── Page header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <TeamOutlined style={{ fontSize: 22 }} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Waitlist Management
            </Title>
            <Text type="secondary" style={{ fontSize: 13 }}>
              Click a name or the View button to manage entries, dispatch, and more.
            </Text>
          </div>
        </div>
        <Space>
          <Segmented
            value={showArchived ? "archived" : "active"}
            onChange={(value) => setShowArchived(value === "archived")}
            options={[
              { label: "Active", value: "active" },
              { label: "Archived", value: "archived" }
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Search by name or slug"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 240 }}
          />
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} loading={isFetching} onClick={fetchWaitlistConfigs} />
          </Tooltip>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreateModal}>
            Create Waitlist
          </Button>
        </Space>
      </div>

      {/* ── Table ── */}
      <Table
        columns={columns}
        dataSource={visibleWaitlistConfigs}
        rowKey="_id"
        loading={isFetching}
        bordered
        pagination={false}
        scroll={{ x: 1200 }}
        onRow={(record) => ({
          style: { cursor: "pointer" },
          onClick: () => openWaitlistDetail(record)
        })}
      />

      {isConfigModalOpen && (
        <CreateEditConfigModal
          isModalOpen={isConfigModalOpen}
          isEdit={Boolean(activeWaitlistConfig)}
          isSubmitting={isSubmittingConfig}
          initialValues={activeWaitlistConfig || {}}
          formOptions={waitlistFormOptions}
          setIsModalOpen={setIsConfigModalOpen}
          handleSubmit={handleSubmitConfig}
          refreshFormOptions={fetchWaitlistFormOptions}
          isRefreshingFormOptions={isRefreshingFormOptions}
        />
      )}
    </div>
  );
};

export default WaitlistDashboard;
